/**
 * (De)serialize a cyclic object. The keys "__c", "__i", and "__m" are reserved. If an object has a tag of __c,
 * this is removed from the deserialized result and the object is assigned that index; then, things of the form
 * { __i: i } are replaced by references to the object at index i. "__m" is used to indicate maps. The lowest bit of
 * the index of a circular reference is 1 iff the object is a Map. This is done to allow for circular references to
 * a map.
 *
 * Subclasses of BaseTree are omitted from the output because we don't want to send JDK AST info to students.
 *
 * This doesn't have to be perfect: It is only used to (de)serialize the JDK info.
 */
import {cafebabeAssert} from "./error";
import {BaseTree} from "./model";

function cyclicSerializeImpl(
	object: any,
	outJson: string[],
	refs: Map<object, number>,
	usedRefs: Set<number>,
	counter: {next: number},
	genUsage: boolean
) {
	if (typeof object !== "object" || object === null) {
		// Serialize via default implementation if not an object, but use "null" for undefined
		outJson.push(object === undefined ? "null" : JSON.stringify(object));
		return;
	}
	if (object instanceof BaseTree) {
		// Omit AST info (should always be optional/nullable in the type model)
		outJson.push("null");
		return;
	}
	cafebabeAssert(
		!("__i" in object || "__c" in object || "__m" in object),
		"cyclicSerialize requirement"
	);
	const existing = refs.get(object);
	if (existing !== undefined) {
		if (usedRefs) usedRefs.add(existing);
		outJson.push(`{"__i":${existing}}`);
		return;
	}

	if (Array.isArray(object)) {
		outJson.push("[");
		for (let i = 0; i < object.length; i++) {
			if (i !== 0) outJson.push(",");
			cyclicSerializeImpl(
				object[i],
				outJson,
				refs,
				usedRefs,
				counter,
				genUsage
			);
		}
		outJson.push("]");
		return;
	}
	// make the lowest bit 1 if the object is a map
	const index = (counter.next++ << 1) | +(object instanceof Map);
	let needsComma = true;
	if (!genUsage && !usedRefs.has(index)) {
		outJson.push("{");
		needsComma = false;
	} else {
		refs.set(object, index);
		outJson.push(`{"__c":${index}`); // the following object is cyclic
	}
	if (object instanceof Map) {
		needsComma && outJson.push(",");
		outJson.push(`"__m":1`);
		for (const [key, value] of object.entries()) {
			outJson.push(`,${JSON.stringify(key)}:`);
			cyclicSerializeImpl(value, outJson, refs, usedRefs, counter, genUsage);
		}
	} else {
		for (const key of Object.keys(object)) {
			needsComma && outJson.push(",");
			needsComma = true;
			outJson.push(`${JSON.stringify(key)}:`);
			cyclicSerializeImpl(
				object[key],
				outJson,
				refs,
				usedRefs,
				counter,
				genUsage
			);
		}
	}
	outJson.push("}");
}

function deserializeImpl(object: any, refs: any[]): any {
	if (object === null) return null;
	if ("__i" in object) {
		cafebabeAssert(
			typeof object.__i === "number",
			"cyclicDeserialize requirement 2"
		);
		return (refs[object.__i] ??= {});
	}
	let target: object | undefined = undefined;
	if ("__c" in object) {
		cafebabeAssert(
			typeof object.__c === "number",
			"cyclicDeserialize requirement 3"
		);
		target = refs[object.__c] ??= object.__c & 1 ? new Map() : {}; // make the object a map if the lowest bit is 1
		delete object.__c;
	}
	for (const key of Object.keys(object))
		if (typeof object[key] === "object")
			object[key] = deserializeImpl(object[key], refs);
	if ("__m" in object) {
		delete object.__m;
		target ??= new Map();
		cafebabeAssert(target instanceof Map, "cyclicDeserialize requirement 4");
		for (const [k, v] of Object.entries(object)) {
			(target as Map<any, any>).set(k, v);
		}
	} else if (target) Object.assign(target, object);
	return target ?? object;
}

/**
 * Deserialize the cyclically-serialized object.
 * @param json JSON string.
 */
export function cyclicDeserialize(json: string | object) {
	const object: any = typeof json === "string" ? JSON.parse(json) : json;
	cafebabeAssert(typeof object === "object", "cyclicDeserialize requirement 1");
	return deserializeImpl(object, []);
}

/**
 * Serialize the possibly-cyclic object by adding sentinel values for cycles. Removes instances of BaseTree from the
 * output.
 * @param object Object to serialize.
 */
export function cyclicSerialize(object: object) {
	const outJson: string[] = [];
	const usedRefs = new Set<number>();
	// collect usage info
	cyclicSerializeImpl(object, [], new Map(), usedRefs, {next: 0}, true);
	// Actually serialize
	cyclicSerializeImpl(object, outJson, new Map(), usedRefs, {next: 0}, false);
	return outJson.join("");
}
